import axios from "axios";
import { environment } from "../../environments/environment";
import { omitEmpty } from "../utils/object-utils";
import { isNetworkError, NetworkError } from "../utils/errors";
import { logger } from "./analytics";

export interface Job {
    id: number,
    jobpostId: number,
    jobTitle: {
        id: any,
        name: string
    },
    city: {
        id: any,
        displayName: string
    },
    company: {
        id: any,
        name: string,
        city: {
            id: any,
            displayName: string,
        }
    },
    salary: number,
    showSalary: boolean,
    jobType: string,
    jobDescription: string,
    startDate: string,
    createdAt: string,
    updatedAt: string,
    publishDate: string,
    admin: Record<string, any>,
    contactEmail: string,
    contactName: string,
    status: string,
    escola_do_mecanico: Record<string, any>,
    escola_do_mecanico_id?: number,
    jobpostEmId?: number,
    candidatesCount?: number
};

interface Params {
    offset?: number,
    limit?: number,
    status?: string,
    search?: string,
    id?: number,
    managerId?: number,
    jobType?: string,
    jobpostEmId?: number,
    signal?: AbortSignal,
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const retryOnNetworkError = async <T,>(
    fn: () => Promise<T>,
    retries: number,
    context: Record<string, any>,
    signal?: AbortSignal
): Promise<T> => {
    try {
        return await fn();
    } catch (error) {
        // Não tenta novamente se o signal já foi abortado (busca/contexto obsoleto)
        if (retries > 0 && isNetworkError(error) && !signal?.aborted) {
            logger.warn('Erro de rede ao buscar vagas - tentando novamente', {
                ...context,
                remaining_retries: retries,
                category: 'api'
            });
            await delay(800);
            // Checa novamente após o delay caso o signal tenha sido abortado enquanto aguardava
            if (signal?.aborted) throw error;
            return retryOnNetworkError(fn, retries - 1, context, signal);
        }
        throw error;
    }
};

export const listJobs = async (params?: Params): Promise<{
    totalCount: number,
    data: Job[]
}> => {
    const args: string[] = ['orderBy: "publish_date_desc"'];
    if (params?.offset)      args.push(`offset: ${params.offset}`);
    if (params?.limit)       args.push(`first: ${params.limit}`);
    if (params?.status)      args.push(`status: ${params.status}`);
    if (params?.search)      args.push(`search: "${params.search}"`);
    if (params?.managerId)   args.push(`managerId: "${params.managerId}"`);
    if (params?.id)          args.push(`id: ${params.id}`);
    if (params?.jobType)     args.push(`jobType: ${params.jobType}`);
    if (params?.jobpostEmId) args.push(`jobpostEmId: ${params.jobpostEmId}`);

    try {
        const response = await retryOnNetworkError(
            () => axios.post(`${environment.apiUrl}/graphql`,
                {
                  "operationName": "AllJobs",
                            "query": `query AllJobs {
                                allJobs(${args.join(', ')}) {
                                    edges {
                                        node {
                                            id
                                            jobpostId,
                                            company {
                                                id,
                                                name,
                                                city {
                                                    id,
                                                    displayName,
                                                    uf
                                                }
                                            },
                                            jobTitle {
                                                id
                                                name
                                            }
                                            salary
                                            jobType
                                            status
                                            jobDescription
                                            startDate
                                            updatedAt
                                            publishDate
                                            showSalary
                                            comission
                                            quantity
                                            minimumEducation
                                            minimumExperience
                                            recommendedCourses {
                                                id
                                                name
                                                institution
                                            }
                                            jobSkills {
                                                id
                                                skillType
                                                skill {
                                                    id
                                                    name
                                                    description
                                                    categoryName
                                                }
                                            }
                                            jobBenefits {
                                                id
                                                value
                                                timeUnit
                                                description
                                                jobbenefitId
                                                benefitId
                                                benefit {
                                                    id
                                                    name
                                                    simplifiedName
                                                    hasValue
                                                    hasTimeUnit
                                                }
                                            }
                                            jobpostEmId
                                            candidatesCount
                                        }
                                    },
                                    totalCount
                                }
                            }`,
                  "variables": {}
                }, {
                  headers: {
                    "content-type": "application/json"
                  },
                  timeout: 15000,
                  signal: params?.signal
                }
            ),
            1,
            {
                endpoint: '/graphql',
                operation: 'AllJobs',
                job_id: params?.id
            },
            params?.signal
        );

        if(response.status !== 200) {
            throw Error('error');
        };

        return {
            data: response.data.data.allJobs.edges.map((x: any) => ({
                ...x.node,
                candidatesCount: x.node.candidatesCount || 0
            })),
            totalCount: response.data.data.allJobs.totalCount
        };
    } catch (error) {
        if (isNetworkError(error)) {
            logger.warn('Erro de rede ao buscar vagas', {
                job_id: params?.id,
                category: 'api',
                error_code: (error as any)?.code,
                error_message: (error as any)?.message,
                http_status: (error as any)?.response?.status
            });
            throw new NetworkError(
                'Erro de conexão ao buscar vagas. Verifique sua internet e tente novamente.',
                {
                    originalError: error,
                    httpStatus: (error as any)?.response?.status,
                    code: (error as any)?.code
                }
            );
        }
        throw error;
    }
};

export const createJob = async (data: any): Promise<any> => {
    const cleanedData = omitEmpty(data);
    const response = await axios.post<any>(environment.apiUrl+'/api/jobpost_new/register/', cleanedData, {
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if(response.status !== 200) {
        throw Error('error');
    };

    return response.data;
};

export const updateJob = async (id: any, data: any): Promise<any> => {
    const response = await axios.put<any>(environment.apiUrl+'/api/jobpost/'+id+'/', data, {
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if(response.status !== 200) {
        throw Error('error');
    };

    return response.data;
};

export const removeJob = async (id: number, data: {reason_id: number}): Promise<any> => {
    const response = await axios.post<any>(environment.apiUrl+'/api/jobpost_new/remove/'+id, data, {
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if(response.status !== 200) {
        throw Error('error');
    };

    return response.data;
};

export const listJobTitles = async (): Promise<{
    id: string,
    name: string
}[]> => {
    const response = await axios.post(`${environment.apiUrl}/graphql`,
        {
          "operationName": "AllJobs",
          "query": `query AllJobs {
                        allJobTitles {
                            id
                            name
                        }
                    }`,
          "variables": {}
        }, {
          headers: {
            "content-type": "application/json"
          }
        }
    );

    if(response.status !== 200) {
        throw Error('error');
    };

    return response.data.data.allJobTitles;
};